import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import { hotelCampaignRegistrationStatuses, hotelStatusNamesFilterable } from 'main/hotelStatuses'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getHealthCheckInvoices } from '../../../actions/healthCheck'
import CustomHeader from '../../../components/ReusableComponents/HeaderTitles'
import LoadingIndicator from '../../../components/ReusableComponents/LoadingIndicator'
import Table from '../../../components/ReusableComponents/Table'
import Tab from '../../../components/ReusableComponents/Tabs'
import Toggle from '../../../components/ReusableComponents/Toggle/index'
import downloadAsCsv from '../../../utils/downloadAsCsv'
import { formatPrice } from '../../../utils/common'
import HealthCheckFilters from '../Common/Filters'
import useHealthCheckFilters from '../Common/useHealthCheckFilters'
import { ReactComponent as ExcelIcon } from '../Billing/excel.svg'
import styles from './Performance.module.scss'
import InvoicesRow from './Row'
import InvoicesTitleRow from './TitleRow'
import TotalRow from './TotalRow'

const HealthCheckPerformance = () => {
  const dispatch = useDispatch()

  const [results, setResults] = useState([])
  const [totalBarData, setTotalBarData] = useState()

  const [filterState, filterDispatch] = useHealthCheckFilters('performance')

  const [highlightNotSent, setHighlightNotSent] = useState(true)
  const [highlightPaymentPending, setHighlightPaymentPending] = useState(true)

  // Alert status filter toggles - persisted in localStorage
  const { showAlert, showWarning, showGreen } = filterState

  // Calculate row status for Performance tab
  const calculateRowStatus = row => {
    const { items, months } = row

    if (!items || typeof items !== 'object') {
      return 'warning'
    }

    let alertCount = 0
    let warningCount = 0
    let greenCount = 0

    // Check each month's invoice status
    Object.values(items).forEach(item => {
      if (!item || !item.status) return

      const status = item.status
      // Alert statuses
      if (
        status === 'PaymentOverdue' ||
        status === 'PaymentFailed' ||
        status === 'ReviewCompleted' ||
        status === 'ReviewPending'
      ) {
        alertCount++
      }
      // Warning statuses
      else if (status === 'PaymentPending' || status === 'PaidPartially') {
        warningCount++
      }
      // Green statuses
      else if (status === 'Paid') {
        greenCount++
      }
    })

    // ALERT conditions:
    // 1. Multiple unpaid invoices (2+ alerts)
    if (alertCount >= 2) {
      return 'alert'
    }

    // 2. At least one overdue or failed payment
    if (alertCount > 0) {
      return 'alert'
    }

    // WARNING conditions:
    // 1. Some pending payments or partially paid
    if (warningCount > 0) {
      return 'warning'
    }

    // 2. One alert but not multiple
    if (alertCount === 1) {
      return 'warning'
    }

    // GREEN conditions:
    // All paid or in progress
    if (alertCount === 0 && warningCount === 0 && greenCount > 0) {
      return 'green'
    }

    // Default to warning if status is unclear
    return 'warning'
  }

  // Generate explanation for row status
  const getStatusExplanation = row => {
    const { items } = row

    if (!items || typeof items !== 'object') {
      return 'No invoice data available'
    }

    let alertCount = 0
    let warningCount = 0
    let greenCount = 0
    const alertStatuses = []
    const warningStatuses = []

    Object.values(items).forEach(item => {
      if (!item || !item.status) return

      const status = item.status
      if (status === 'PaymentOverdue' || status === 'PaymentFailed' || 
          status === 'ReviewCompleted' || status === 'ReviewPending') {
        alertCount++
        alertStatuses.push(status)
      } else if (status === 'PaymentPending' || status === 'PaidPartially') {
        warningCount++
        warningStatuses.push(status)
      } else if (status === 'Paid') {
        greenCount++
      }
    })

    const status = calculateRowStatus(row)
    const reasons = []

    if (status === 'alert') {
      if (alertCount >= 2) {
        reasons.push(`${alertCount} unpaid invoices`)
      }
      if (alertStatuses.includes('PaymentOverdue')) {
        reasons.push('Payment overdue')
      }
      if (alertStatuses.includes('PaymentFailed')) {
        reasons.push('Payment failed')
      }
      if (alertStatuses.includes('ReviewPending') || alertStatuses.includes('ReviewCompleted')) {
        reasons.push('Invoice not sent')
      }
    } else if (status === 'warning') {
      if (warningCount > 0) {
        reasons.push(`${warningCount} pending/partially paid invoice(s)`)
      }
      if (alertCount === 1) {
        reasons.push('1 unpaid invoice')
      }
    } else if (status === 'green') {
      reasons.push('All invoices paid or in progress')
      if (greenCount > 0) {
        reasons.push(`${greenCount} paid invoice(s)`)
      }
    }

    return reasons.length > 0 ? reasons.join(' • ') : 'Status: ' + status
  }

  useEffect(() => {
    dispatch(getHealthCheckInvoices())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = useSelector(s => s.healthCheck.invoices.isLoading)
  const hotelData = useSelector(s => s.healthCheck.invoices.hotelData)
  const months = useSelector(s => s.healthCheck.invoices.months)
  const allhotelGroups = useSelector(s => s.healthCheck.invoices.hotelGroups)
  const allhotelChains = useSelector(s => s.healthCheck.invoices.hotelChains)

  const [sortByIndex, setSortByIndex] = useState(1)
  const [sortDecreasing, setSortDecreasing] = useState(true)

  function handleTitleCellClick(rowIndex) {
    if (sortByIndex === rowIndex) {
      setSortDecreasing(d => !d)
    }

    setSortByIndex(rowIndex)
  }

  useEffect(() => {
    function getFilteredResults(hotelData) {
      const { searchString, chains, brands, hotelGroups, subsidiaryStatus, csmIds } =
        filterState
      const filtered = hotelData
        .filter(h => h.searchKey.match(new RegExp(searchString, 'i')))
        .filter(h => (hotelGroups === 'all' ? true : h.groupName === hotelGroups))
        .filter(h => (chains === 'all' ? true : h.chainCode === chains))
        .filter(h => (brands === 'all' ? true : h.parentChainCode === brands))
        .filter(h => {
          if (subsidiaryStatus.length === 0) {
            return true
          } else if (hotelCampaignRegistrationStatuses.includes(h.subsidiaryStatus)) {
            return subsidiaryStatus.includes('PendingCampaignBrandRegistration')
          } else {
            return subsidiaryStatus.includes(h.subsidiaryStatus)
          }
        })
        .filter(h => {
          if (csmIds.length === 0) {
            return true
          } else {
            return csmIds.includes(`${h.csmUserId}`)
          }
        })

      // Apply status filter
      return filtered.filter(h => {
        const status = calculateRowStatus(h)
        if (status === 'alert') return showAlert
        if (status === 'warning') return showWarning
        if (status === 'green') return showGreen
        return true
      })
    }

    function getSortedResults(hotelData, sortBy, sortDecreasing) {
      const titleRowNames = ['displayId', 'shortName', ...months]
      const sortByProp = titleRowNames[sortBy]
      const sortedData = hotelData.sort((a, b) => {
        let aVal = a[sortByProp]
        let bVal = b[sortByProp]

        if (sortBy > 1) {
          aVal = a.items?.[sortByProp]?.totalAmountDue || 0
          bVal = b.items?.[sortByProp]?.totalAmountDue || 0
        }

        if (typeof aVal === 'string') {
          if (aVal === bVal) {
            return 0
          }
          return aVal?.toUpperCase() > bVal?.toUpperCase()
        } else if (typeof aVal === 'boolean') {
          return Number(bVal) - Number(aVal)
        } else {
          return bVal - aVal
        }
      })
      return sortDecreasing ? sortedData : sortedData.reverse()
    }

    function getResults() {
      const results = getSortedResults(
        getFilteredResults(hotelData),
        sortByIndex,
        sortDecreasing
      )

      const totalBarData = months.reduce(
        (carried, currentMonth) => {
          return {
            ...carried,
            [currentMonth]: results.reduce(
              (prev, curr) => {
                return {
                  chargedBookings:
                    prev.chargedBookings +
                    (curr?.items?.[currentMonth]?.bookingCountToCharge || 0),
                  nonChargedBookings:
                    prev.nonChargedBookings +
                    (curr?.items?.[currentMonth]?.bookingCountNotCharged || 0),
                  transactionVolume:
                    prev.transactionVolume +
                    (curr?.items?.[currentMonth]?.totalBookingValueToCharge || 0),
                  total: prev.total + (curr?.items?.[currentMonth]?.totalAmountDue || 0),
                  count:
                    prev.count +
                    (curr?.items?.[currentMonth]?.totalAmountDue >= 0 ? 1 : 0)
                }
              },
              {
                total: 0,
                count: 0,
                chargedBookings: 0,
                nonChargedBookings: 0,
                transactionVolume: 0
              }
            )
          }
        },
        months.reduce((prev, curr) => ({ ...prev, [curr]: { total: 0, count: 0 } }), {})
      )

      setTotalBarData({ data: totalBarData, count: results.length })
      setResults(results)
    }
    getResults()
  }, [filterState, hotelData, months, sortByIndex, sortDecreasing, showAlert, showWarning, showGreen])

  function handleClickDownloadAsCsv() {
    // Get filter values for metadata
    const {
      searchString,
      chains,
      brands,
      hotelGroups,
      subsidiaryStatus,
      csmIds
    } = filterState

    // Helper function to get filter label
    const getFilterLabel = (value, allOptions, defaultLabel) => {
      if (value === 'all' || !value || (Array.isArray(value) && value.length === 0)) {
        return 'All'
      }
      if (Array.isArray(value)) {
        return value.map(v => allOptions[v] || v).join(', ')
      }
      return allOptions[value] || value || defaultLabel
    }

    // Get CSM names if available
    const getCsmLabel = () => {
      if (!csmIds || csmIds.length === 0) return 'All'
      return csmIds.join(', ')
    }

    // Get status labels
    const getStatusLabel = () => {
      if (!subsidiaryStatus || subsidiaryStatus.length === 0) return 'All'
      return subsidiaryStatus.map(s => hotelStatusNamesFilterable[s] || s).join(', ')
    }

    const filterMetadata = [
      ['Filter Values Applied:'],
      ['Hotel Group', getFilterLabel(hotelGroups, allhotelGroups, 'All')],
      ['Brand', getFilterLabel(brands, parentChainNames, 'All')],
      ['Chain', getFilterLabel(chains, allhotelChains, 'All')],
      ['Status', getStatusLabel()],
      ['CSM', getCsmLabel()],
      ['Search', searchString || 'None'],
      ['Status Filters', `Alert: ${showAlert ? 'Yes' : 'No'}, Warning: ${showWarning ? 'Yes' : 'No'}, Green: ${showGreen ? 'Yes' : 'No'}`],
      [] // Empty row separator
    ]

    const titleRow = [
      'Hotel ID',
      'Hotel Name',
      'Hotel Group',
      'Brand',
      'Chain',
      'CSM',
      'Status',
      ...months.map(m => moment(m).format('MMM YYYY')),
      'Service Start Date',
      'Date Added'
    ]

    const resAsArrays = results.map(h => {
      const row = [
        h.displayId,
        `${h.brand || ''} ${h.shortName || ''}`.trim(),
        h.groupName || '-',
        h.parentChainCode || '-',
        h.chainCode || '-',
        h.csmUserFullName || '-',
        h.subsidiaryStatus ? (hotelStatusNamesFilterable[h.subsidiaryStatus] || h.subsidiaryStatus) : '-'
      ]

      months.forEach(month => {
        const item = h.items?.[month]
        if (item) {
          row.push(formatPrice(item.totalAmountDue || 0))
        } else {
          row.push('-')
        }
      })

      row.push(
        h.serviceStartDate
          ? moment(h.serviceStartDate).format('MM/DD/YYYY')
          : '-',
        h.addedOn
          ? moment(h.addedOn).format('MM/DD/YYYY')
          : '-'
      )

      return row
    })

    // Combine metadata, title row, and data
    const dataArray = [
      ...filterMetadata,
      titleRow,
      ...resAsArrays
    ]
    
    const fileName = `health_check_performance_${moment().format('YYYY-MM-DD')}.csv`

    downloadAsCsv(dataArray, fileName)
  }

  return (
    <div className={styles.wrapper}>
      <CustomHeader title="Hotel health check" width={1180} isFixed />
      <Tab.Parent>
        <Tab to="/healthCheck/pms">PMS</Tab>
        <Tab to="/healthCheck/autoinvite">AutoInvite</Tab>
        <Tab to="/healthCheck/billing">Billing</Tab>
        <Tab to="/healthCheck/performance">Performance</Tab>
        <Tab to="/healthCheck/summary">Summary</Tab>
      </Tab.Parent>
      <HealthCheckFilters
        state={filterState}
        dispatch={filterDispatch}
        allhotelGroups={allhotelGroups}
        allhotelChains={allhotelChains}
        showSubsidiaryStatus={true}
        showCsmFilter
      >
        <div className={styles.highlightControls}>
          <span className={styles.highlightControlsTitle}>Highlight:</span>
          <Toggle
            className={styles.toggle}
            checked={highlightNotSent}
            handleChange={e => setHighlightNotSent(e.target.checked)}
            label="Not sent"
          />
          <Toggle
            className={styles.toggle}
            checked={highlightPaymentPending}
            handleChange={e => setHighlightPaymentPending(e.target.checked)}
            label="Payment pending"
          />
        </div>
        <div className={styles.alertStatusControls}>
          <span className={styles.alertStatusControlsTitle}>Filter by Status:</span>
          <Toggle
            className={styles.toggle}
            checked={showAlert}
            handleChange={e => filterDispatch({ type: 'showAlert', payload: e.target.checked })}
            label="Alert"
          />
          <Toggle
            className={styles.toggle}
            checked={showWarning}
            handleChange={e => filterDispatch({ type: 'showWarning', payload: e.target.checked })}
            label="Warning"
          />
          <Toggle
            className={styles.toggle}
            checked={showGreen}
            handleChange={e => filterDispatch({ type: 'showGreen', payload: e.target.checked })}
            label="Green"
          />
        </div>
        <SecondaryRegularButton
          className={styles.copyToExcelButton}
          onClick={() => handleClickDownloadAsCsv()}
        >
          <ExcelIcon /> Download as CSV
        </SecondaryRegularButton>
      </HealthCheckFilters>

      <div className={styles.tableContainer}>
        {isLoading ? (
          <LoadingIndicator marginTop={150} />
        ) : (
          <Table
            widths={[40, 265, 80, 125, 125, 125, 125, 125, 125, 125]}
            handleTitleCellClick={handleTitleCellClick}
            sortIndex={sortByIndex}
            sortDirection={sortDecreasing ? 'down' : 'up'}
          >
            <InvoicesTitleRow months={months} />

            {results.map((h, i) => {
              const rowStatus = calculateRowStatus(h)
              const statusExplanation = getStatusExplanation(h)
              return (
                <InvoicesRow
                  {...h}
                  index={i + 1}
                  months={months}
                  highlightPaymentPending={highlightPaymentPending}
                  highlightNotSent={highlightNotSent}
                  rowStatus={rowStatus}
                  statusExplanation={statusExplanation}
                />
              )
            })}
            <TotalRow
              months={months}
              data={totalBarData?.data}
              count={totalBarData?.count}
            />
          </Table>
        )}
      </div>
    </div>
  )
}

export default HealthCheckPerformance
